export type DifficultyLevel = 'beginner' | 'intermediate' | 'expert';

interface Question {
  question: string;
  answer: number;
  explanation?: string;
}

const generateBeginnerQuestion = (): Question => {
  const operations = [
    // Existing operations
    () => {
      const num = Math.floor(Math.random() * 50) + 51; // 51-100
      return {
        question: `${num} × 5`,
        answer: num * 5,
        explanation: `Tip: Multiply by 10 and divide by 2`
      };
    },
    () => {
      const num = Math.floor(Math.random() * 30) + 11; // 11-40
      return {
        question: `${num} × 11`,
        answer: num * 11,
        explanation: `Tip: For 2-digit numbers, sum of digits goes in middle`
      };
    },
    () => {
      const num = Math.floor(Math.random() * 50) + 51; // 51-100
      return {
        question: `${num} × 25`,
        answer: num * 25,
        explanation: `Tip: Multiply by 100 and divide by 4`
      };
    },
    // New operations
    () => {
      const x = Math.floor(Math.random() * 9) + 1; // 1-9
      const y = Math.floor(Math.random() * 9) + 1; // 1-9
      return {
        question: `${x} × ${y}`,
        answer: x * y,
        explanation: `Basic single-digit multiplication`
      };
    },
    () => {
      const table = Math.floor(Math.random() * 20) + 1; // 1-20
      const multiplier = Math.floor(Math.random() * 10) + 1; // 1-10
      return {
        question: `${table} × ${multiplier}`,
        answer: table * multiplier,
        explanation: `From ${table}'s multiplication table`
      };
    }
  ];

  return operations[Math.floor(Math.random() * operations.length)]();
};

const generateIntermediateQuestion = (): Question => {
  const operations = [
    // Existing operations
    () => {
      const num = Math.floor(Math.random() * 100) + 101; // 101-200
      return {
        question: `${num} × 99`,
        answer: num * 99,
        explanation: `Tip: Multiply by 100 and subtract the original number`
      };
    },
    () => {
      const num = Math.floor(Math.random() * 50) + 51; // 51-100
      return {
        question: `${num} × 125`,
        answer: num * 125,
        explanation: `Tip: Multiply by 1000 and divide by 8`
      };
    },
    () => {
      const base = Math.floor(Math.random() * 10) + 91; // 91-100
      const num = Math.floor(Math.random() * 10) + 91; // 91-100
      return {
        question: `${base} × ${num}`,
        answer: base * num,
        explanation: `Tip: Use (100 - a)(100 - b) method`
      };
    },
    // New operations
    () => {
      const x = Math.floor(Math.random() * 90) + 10; // 10-99
      const y = Math.floor(Math.random() * 90) + 10; // 10-99
      return {
        question: `${x} × ${y}`,
        answer: x * y,
        explanation: `Double-digit multiplication`
      };
    },
    () => {
      const table = Math.floor(Math.random() * 79) + 21; // 21-99
      const multiplier = Math.floor(Math.random() * 10) + 1; // 1-10
      return {
        question: `${table} × ${multiplier}`,
        answer: table * multiplier,
        explanation: `From ${table}'s multiplication table`
      };
    }
  ];

  return operations[Math.floor(Math.random() * operations.length)]();
};

const generateExpertQuestion = (): Question => {
  const operations = [
    // Existing operations
    () => {
      const num = Math.floor(Math.random() * 100) + 901; // 901-1000
      return {
        question: `${num} × 999`,
        answer: num * 999,
        explanation: `Tip: Multiply by 1000 and subtract the original number`
      };
    },
    () => {
      const num = Math.floor(Math.random() * 50) + 151; // 151-200
      return {
        question: `${num} × 625`,
        answer: num * 625,
        explanation: `Tip: Multiply by 1000 and divide by 1.6`
      };
    },
    () => {
      const base = Math.floor(Math.random() * 20) + 981; // 981-1000
      const num = Math.floor(Math.random() * 20) + 981; // 981-1000
      return {
        question: `${base} × ${num}`,
        answer: base * num,
        explanation: `Tip: Use (1000 - a)(1000 - b) method`
      };
    },
    // New operations
    () => {
      const table = Math.floor(Math.random() * 899) + 101; // 101-999
      const multiplier = Math.floor(Math.random() * 10) + 1; // 1-10
      return {
        question: `${table} × ${multiplier}`,
        answer: table * multiplier,
        explanation: `From ${table}'s multiplication table`
      };
    },
    () => {
      const x = Math.floor(Math.random() * 900) + 100; // 100-999
      const y = Math.floor(Math.random() * 90) + 10; // 10-99
      return {
        question: `${x} × ${y}`,
        answer: x * y,
        explanation: `Three-digit by two-digit multiplication`
      };
    }
  ];

  return operations[Math.floor(Math.random() * operations.length)]();
};

export const generateQuestion = (level: DifficultyLevel): Question => {
  switch (level) {
    case 'beginner':
      return generateBeginnerQuestion();
    case 'intermediate':
      return generateIntermediateQuestion();
    case 'expert':
      return generateExpertQuestion();
    default:
      return generateBeginnerQuestion();
  }
};